import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Context
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import { AppContext } from '../contexts/context';
// Material UI
import { CircularProgress, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
// indexedDB
// @ts-ignore
import { idbCreate } from "./indexedDB";
// @ts-ignore
import { getBrandValue, lightenDarkenColor } from "./Utils";
import { getAppUrl, localStorageThemeUriKey } from "./AppUrl";
import { Capacitor } from "@capacitor/core";
const useStyles = makeStyles({
    loading: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: '-50px',
        marginLeft: '-100px',
        width: '200px',
        height: '100px',
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
    },
});
let appBrandsCache = null;
function loadBrandData() {
    if (!appBrandsCache) {
        appBrandsCache = fetch(`/appBrands.json`, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        }).then(r => r.json());
    }
    return appBrandsCache;
}
export const ThemeController = ({ children }) => {
    const classes = useStyles();
    const [state, dispatch] = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [serviceWorkerFinished, setServiceWorkerFinished] = useState(false);
    const [manifestLoaded, setManifestLoaded] = useState(false);
    const [manifestLink, setManifestLink] = useState('/kde.webmanifest');
    const [loadingState, setLoadingState] = useState('...');
    // Firebase storage reference
    const firstUpdateRef = useRef(true);
    useLayoutEffect(() => {
        if (firstUpdateRef.current) {
            idbCreate();
        }
    }, []);
    const primaryLight = lightenDarkenColor(state.brandColor1, -40);
    const primaryDark = lightenDarkenColor(state.brandColor1, 40);
    const secondaryLight = lightenDarkenColor(state.brandColor2, -40);
    const secondaryDark = lightenDarkenColor(state.brandColor2, 40);
    const theme = useMemo(() => createTheme({
        palette: {
            primary: {
                light: primaryLight,
                main: state.brandColor1,
                dark: primaryDark
            },
            secondary: {
                light: secondaryLight,
                main: state.brandColor2,
                dark: secondaryDark
            },
        },
        /*overrides: {
          MuiCard: {
            root: {
              marginLeft: 10,
              marginRight: 10,
              marginTop: 10,
              marginBottom: 0,
            }
          },
        },*/
        components: {
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        marginBottom: 15,
                    },
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontWeight: 500,
                    },
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        fontStyle: 'italic'
                    }
                }
            }
        },
        spacing: 8,
    }), [state.brandColor1, state.brandColor2]);
    const onManifestLoaded = () => {
        console.info("ThemeController | appCycleCheck: everything is finished");
        setManifestLoaded(true);
    };
    const appCycleCheck = () => {
        /*if ("serviceWorker" in navigator && process.env.NODE_ENV === 'production') {
          setLoadingState("Service Worker ...");
          navigator.serviceWorker.ready.then((registration) => {
            console.log(`ThemeController | appCycleCheck: A service worker is active:`, registration.active);
            setServiceWorkerFinished(true);
            // At this point, you can call methods that require an active
            // service worker, like registration.pushManager.subscribe()
          });
        } else {
          console.warn("ThemeController | appCycleCheck: Service workers are not supported.");
          setLoadingState("Service Worker skipped!");
          setServiceWorkerFinished(true);
        }*/
        // var manifestSelection = document.querySelector('#manifest-selection')
        // EDIT TST event 'load' -> 'DOMContentLoaded' and run only, if document still loading to prevent race conditions.
        if (document.readyState === 'loading') {
            window.addEventListener('DOMContentLoaded', function () {
                onManifestLoaded();
            });
        }
        else {
            onManifestLoaded();
        }
    };
    const getManifestData = (href) => {
        fetch(href, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        }).then(function (response) {
            return response.json();
        }).then(function (myJson) {
            console.log('ThemeController | manifestData:', myJson);
            dispatch({
                type: 'app/brand/manifest/updated',
                appName: myJson.name,
                appNameShort: myJson.short_name
            });
            console.debug("ThemeController | loading done");
            setLoading(false);
        });
    };
    const selectBrand = (appBrands, brandValue) => {
        const brandData = appBrands.brands[brandValue];
        if (!brandData) {
            console.error('ThemeController | unknown brandValue:', brandValue);
            return;
        }
        console.log('ThemeController | brandData:', brandData);
        dispatch({ type: 'app/brand/logoUrl/updated', value: `image/${brandValue}/logo.png` });
        dispatch({
            type: 'app/brand/data/updated',
            brandName: brandData.Name,
            brandTitle: brandData.Title,
            brandShowLabel: brandData.ShowLabelOnIOs && Capacitor.getPlatform() == 'ios',
            brandColor1: brandData.Color1,
            brandColor2: brandData.Color2,
            brandOwner: brandData.Owner,
            brandStreet: brandData.Street,
            brandZip: brandData.ZipCode,
            brandCity: brandData.City,
            brandMail: brandData.Mail,
            brandPhone: brandData.Phone
        });
    };
    async function loadAndSelectBrand(brand) {
        const appBrands = await loadBrandData();
        selectBrand(appBrands, brand);
    }
    useEffect(() => {
        appCycleCheck();
        if (serviceWorkerFinished) {
            setLoadingState("Theme ...");
            const brandValue = getBrandValue(getAppUrl().toString());
            console.debug('ThemeController | brandValue:', brandValue);
            setManifestLink(`/${brandValue}.webmanifest`);
            loadAndSelectBrand(brandValue);
            if (manifestLoaded) {
                if (manifestLink !== undefined) { // EDIT TST -> undefined
                    console.log("ThemeController | manifestLink:", manifestLink);
                    getManifestData(manifestLink);
                }
                else {
                    console.error('No href for webmanifest found!');
                }
            }
        }
    }, [manifestLink, manifestLoaded, serviceWorkerFinished]);
    // Update Theme depending on last scanned uri on iOS
    useEffect(() => {
        if (Capacitor.getPlatform() !== 'ios' || state.themeUri === null)
            return;
        localStorage.setItem(localStorageThemeUriKey, state.themeUri);
        const brand = getBrandValue(state.themeUri);
        loadAndSelectBrand(brand);
    }, [state.themeUri]);
    if (loading) {
        return (_jsx(ThemeProvider, { theme: theme, children: _jsxs("div", { className: classes.loading, children: [_jsx(CircularProgress, { color: "secondary" }), _jsxs(Typography, { sx: {
                            marginTop: 2,
                        }, children: ["Loading ", loadingState] })] }) }));
    }
    else {
        return (_jsx(ThemeProvider, { theme: theme, children: _jsx(_Fragment, { children: children }) }));
    }
};
